import type { ImageRef } from "./events/files";
import * as api from "./client";

/**
 * Events Media admin transport (DEV-2878, `add-events-home-page`).
 *
 * Every call goes to staff-bff, which proxies content-api across the public → private boundary
 * (design D7). The staff list is `GET /v1/events/staff` — ALL events regardless of status or
 * start date — not the public `GET /v1/events`, which is published-and-upcoming only. The spec
 * requires a cancelled event to "remain visible and editable in the staff admin screen", and
 * staff must be able to correct an event whose date has already passed.
 *
 * There is no cache at any layer (design D10): a save is immediately the current state.
 */

/** Exactly seven values — see the event-management spec, "Event type vocabulary". */
export const EVENT_TYPES = [
  "GC Summit",
  "GC Powerlist",
  "Forum",
  "Awards",
  "Webinar",
  "Roundtable",
  "Dinner",
] as const;

export type EventType = (typeof EVENT_TYPES)[number];

export const EVENT_STATUSES = ["published", "draft", "cancelled"] as const;

export type EventStatus = (typeof EVENT_STATUSES)[number];

export type StaffEvent = {
  id: string;
  title: string;
  type: EventType;
  /** YYYY-MM-DD */
  startDate: string;
  /** HH:MM:SS, local to `timezone` */
  startTime: string;
  /** IANA name, e.g. "Europe/London" */
  timezone: string;
  locationShort: string | null;
  venueAddress: string | null;
  featuredImage: ImageRef | null;
  linkUrl: string;
  status: EventStatus;
  createdAt: string;
  updatedAt: string;
};

export type EventPayload = {
  title: string;
  type: EventType;
  startDate: string;
  startTime: string;
  timezone: string;
  locationShort: string | null;
  venueAddress: string | null;
  featuredImage: ImageRef | null;
  linkUrl: string;
  status: EventStatus;
};

export type EventsPagination = {
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
};

export type StaffEventsResponse = {
  events: StaffEvent[];
  pagination: EventsPagination;
  requestId?: string | null;
};

export type EventSingleResponse = {
  event: StaffEvent;
  requestId?: string | null;
};

/**
 * The four columns content-api will sort on. There is deliberately no entry for location, link
 * or image: they are not useful orderings and the API rejects them.
 */
export const EVENT_SORT_FIELDS = ["startDate", "title", "type", "status"] as const;

export type EventSortField = (typeof EVENT_SORT_FIELDS)[number];

export type EventSortDirection = "asc" | "desc";

export type StaffEventsFilters = {
  q?: string;
  limit?: number;
  offset?: number;
  /**
   * Omit to get content-api's default order — upcoming events soonest first, then past events
   * most recently passed first. Sending a `sortField` replaces that order entirely, so the page
   * must not send one until the staff user picks a column.
   */
  sortField?: EventSortField;
  sortDirection?: EventSortDirection;
};

export type EventHeroBanner = {
  id: string;
  backgroundImage: ImageRef;
  title: string;
  dateText: string;
  locationText: string;
  ctaText: string;
  ctaUrl: string;
  /**
   * Whether the banner is on the public events page. The staff read returns the banner whatever
   * this is — a hidden banner must stay visible and editable here, or there would be no way to
   * put it back up.
   */
  showOnSite: boolean;
  createdAt: string;
  updatedAt: string;
};

export type EventHeroBannerPayload = {
  backgroundImage: ImageRef;
  title: string;
  dateText: string;
  locationText: string;
  ctaText: string;
  ctaUrl: string;
  /**
   * content-api treats this as optional and leaves the stored value alone when it is missing,
   * so that a text edit cannot silently take a banner down. This screen always knows what the
   * control is set to, so it always sends it rather than relying on that fallback.
   */
  showOnSite: boolean;
};

export type EventHeroBannerResponse = {
  /** null until Events Media have configured a banner for the first time. */
  heroBanner: EventHeroBanner | null;
  requestId?: string | null;
};

function buildQuery(filters?: StaffEventsFilters): string {
  const params = new URLSearchParams();
  if (filters?.q) params.append("q", filters.q);
  if (filters?.limit !== undefined) params.append("limit", String(filters.limit));
  if (filters?.offset !== undefined) params.append("offset", String(filters.offset));
  // Both or neither: a direction without a field is meaningless to content-api, and a field
  // sent by accident would silently discard the default upcoming-then-past order.
  if (filters?.sortField) {
    params.append("sortField", filters.sortField);
    params.append("sortDirection", filters.sortDirection ?? "asc");
  }
  const query = params.toString();
  return query ? `?${query}` : "";
}

export const eventsService = {
  /** ALL events — past, draft and cancelled included. Staff need to see and correct them. */
  listForStaff: (filters?: StaffEventsFilters): Promise<StaffEventsResponse> =>
    api.get<StaffEventsResponse>(`/v1/events/staff${buildQuery(filters)}`),

  getById: async (id: string): Promise<StaffEvent> => {
    const response = await api.get<EventSingleResponse>(`/v1/events/${id}`);
    return response.event;
  },

  create: async (payload: EventPayload): Promise<StaffEvent> => {
    const response = await api.post<EventSingleResponse>("/v1/events", toRequestBody(payload));
    return response.event;
  },

  update: async (id: string, payload: EventPayload): Promise<StaffEvent> => {
    const response = await api.put<EventSingleResponse>(
      `/v1/events/${id}`,
      toRequestBody(payload, { nullable: true }),
    );
    return response.event;
  },

  remove: (id: string): Promise<void> => api.del(`/v1/events/${id}`),
};

export const eventHeroBannerService = {
  get: (): Promise<EventHeroBannerResponse> =>
    api.get<EventHeroBannerResponse>("/v1/event-hero-banner"),

  save: (payload: EventHeroBannerPayload): Promise<EventHeroBannerResponse> =>
    api.put<EventHeroBannerResponse>("/v1/event-hero-banner", payload),
};

/**
 * content-api's create schema takes the optional fields as `undefined`-or-present, while its
 * update schema takes them as nullable (a null clears the stored value). Sending `null` on a
 * create would fail validation, so the empty slots are dropped there and sent as `null` on an
 * update — which is what lets staff remove a featured image or a venue address.
 */
function toRequestBody(
  payload: EventPayload,
  options: { nullable?: boolean } = {},
): Record<string, unknown> {
  const body: Record<string, unknown> = {
    title: payload.title,
    type: payload.type,
    startDate: payload.startDate,
    startTime: payload.startTime,
    timezone: payload.timezone,
    linkUrl: payload.linkUrl,
    status: payload.status,
  };

  const optional: Array<[keyof EventPayload, unknown]> = [
    ["locationShort", payload.locationShort],
    ["venueAddress", payload.venueAddress],
    ["featuredImage", payload.featuredImage],
  ];

  for (const [key, value] of optional) {
    if (value !== null && value !== undefined && value !== "") {
      body[key] = value;
    } else if (options.nullable) {
      body[key] = null;
    }
  }

  return body;
}
